import { useState } from "react";
import { motion } from "framer-motion";
import { Rocket } from "lucide-react";
import { DetailModal } from "@/components/site/DetailModal";

const SPOKES = [
  {
    label: "Strategy",
    color: "#FF8800",
    detail:
      "The master plan of the venture. LaunchLab's strategy practice defines where the business will play and how it will win — market selection, competitive positioning, business model design and the roadmap from idea to institution.",
    bullets: ["Market entry and positioning strategy", "Business model and revenue architecture", "12–24 month venture roadmap"],
  },
  {
    label: "Technology",
    color: "#00B0FF",
    detail:
      "The digital backbone. Our technology team architects and builds websites, apps, e-commerce platforms, business software, automation and AI-enabled systems that scale with the venture from day one.",
    bullets: ["Web, mobile and e-commerce engineering", "Business software and automation", "AI-enabled solutions and integrations"],
  },
  {
    label: "Branding",
    color: "#00E676",
    detail:
      "How the world sees the business. Naming, identity, logo, positioning, packaging and communication — a complete brand system that builds trust and recall from the very first impression.",
    bullets: ["Naming, logo and identity systems", "Positioning and packaging", "Communication and brand voice"],
  },
  {
    label: "Finance",
    color: "#FF8800",
    detail:
      "The numbers that keep the venture alive. Financial modelling, pricing, unit economics, cash-flow planning and funding readiness — so founders always know exactly where the business stands.",
    bullets: ["Financial modelling and projections", "Pricing and unit economics", "Funding and investor readiness"],
  },
  {
    label: "Marketing",
    color: "#00B0FF",
    detail:
      "The demand engine. Social media, performance marketing, content, influencer campaigns and acquisition funnels that produce a predictable stream of customers instead of occasional spikes.",
    bullets: ["Performance marketing campaigns", "Content and social media systems", "Influencer and growth campaigns"],
  },
  {
    label: "Operations",
    color: "#00E676",
    detail:
      "Daily execution, systemised. Procurement, inventory, customer management, vendors, employees and branch operations — practical operating systems that keep the business running like an institution.",
    bullets: ["Procurement and vendor systems", "Inventory and process design", "Team, SOP and branch operations"],
  },
  {
    label: "Product",
    color: "#FF8800",
    detail:
      "What the customer actually buys. LaunchLab takes concepts through prototyping, sourcing, testing and refinement until a real, market-ready product or service exists — not just a pitch deck.",
    bullets: ["Prototyping and product engineering", "Sourcing and manufacturing tie-ups", "Testing and iteration cycles"],
  },
  {
    label: "Sales",
    color: "#00B0FF",
    detail:
      "Where demand becomes revenue. Sales strategy, conversion playbooks, distribution channels and partner networks that turn marketing interest into signed customers and repeat business.",
    bullets: ["Sales strategy and playbooks", "Distribution channel development", "B2B and partner networks"],
  },
  {
    label: "Logistics",
    color: "#00E676",
    detail:
      "The physical movement of the business. Warehousing, shipping, last-mile delivery and supply-chain design that get products to customers fast, reliably and profitably.",
    bullets: ["Supply-chain and warehousing design", "Shipping and last-mile partnerships", "Delivery cost optimisation"],
  },
  {
    label: "Growth",
    color: "#FF8800",
    detail:
      "The long game. Post-launch, LaunchLab drives systematic improvement across revenue, customers, operations, technology and distribution — turning launched businesses into scaling institutions.",
    bullets: ["Revenue growth programs", "Distribution and market expansion", "Scale and systems upgrades"],
  },
];

export function Ecosystem() {
  const [selected, setSelected] = useState(null);

  return (
    <section id="ecosystem" data-testid="ecosystem-section" className="relative py-20 sm:py-28 lg:py-32 overflow-hidden">
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,rgba(0,230,118,0.04),transparent_55%)] pointer-events-none" />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.8 }}
          className="text-center max-w-3xl mx-auto mb-16"
        >
          <p className="font-mono-tech text-xs tracking-[0.35em] text-[#00B0FF] uppercase mb-4">03 — The Model</p>
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-white" data-testid="ecosystem-headline">
            ONE ECOSYSTEM. <span className="text-gradient-saffron">MANY POSSIBILITIES.</span>
          </h2>
          <p className="mt-5 text-base sm:text-lg text-slate-400 leading-relaxed">
            Entrepreneurs often need a dozen specialists to build a successful company.
            LaunchLab brings those capabilities together — connected to one core. Select any node to explore it.
          </p>
        </motion.div>

        {/* Desktop hub diagram */}
        <motion.div
          initial={{ opacity: 0, scale: 0.92 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.9 }}
          className="hidden md:block relative aspect-square max-w-[680px] mx-auto"
          data-testid="ecosystem-hub"
        >
          <svg viewBox="0 0 100 100" className="absolute inset-0 w-full h-full">
            {SPOKES.map((_, i) => {
              const angle = (i / SPOKES.length) * Math.PI * 2 - Math.PI / 2;
              const x = 50 + 42 * Math.cos(angle);
              const y = 50 + 42 * Math.sin(angle);
              return (
                <line
                  key={i}
                  x1="50"
                  y1="50"
                  x2={x}
                  y2={y}
                  stroke={SPOKES[i].color}
                  strokeOpacity="0.35"
                  strokeWidth="0.25"
                  className="flow-line"
                />
              );
            })}
            <circle cx="50" cy="50" r="42" fill="none" stroke="rgba(255,255,255,0.06)" strokeWidth="0.2" />
            <circle cx="50" cy="50" r="30" fill="none" stroke="rgba(255,255,255,0.04)" strokeWidth="0.2" strokeDasharray="1 1.5" />
          </svg>

          <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-10">
            <div className="w-36 h-36 lg:w-44 lg:h-44 rounded-full glass flex flex-col items-center justify-center text-center shadow-[0_0_80px_rgba(255,136,0,0.25)] animate-float">
              <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-[#FF8800] to-[#ff5e00] flex items-center justify-center mb-2">
                <Rocket className="w-5 h-5 text-[#06080E]" strokeWidth={2.4} />
              </div>
              <p className="font-display font-extrabold tracking-[0.14em] text-white text-sm lg:text-base">LAUNCHLAB</p>
              <p className="font-mono-tech text-[8px] lg:text-[9px] tracking-[0.25em] text-slate-400 mt-1">CORE ENGINE</p>
            </div>
          </div>

          {SPOKES.map((spoke, i) => {
            const angle = (i / SPOKES.length) * Math.PI * 2 - Math.PI / 2;
            const left = 50 + 42 * Math.cos(angle);
            const top = 50 + 42 * Math.sin(angle);
            return (
              <button
                key={spoke.label}
                onClick={() => setSelected(spoke)}
                className="absolute -translate-x-1/2 -translate-y-1/2 group focus:outline-none focus-visible:ring-2 focus-visible:ring-[#FF8800] rounded-full"
                style={{ left: `${left}%`, top: `${top}%` }}
                data-testid={`ecosystem-node-${spoke.label.toLowerCase()}`}
              >
                <div
                  className="glass px-4 py-2.5 rounded-full font-display font-semibold text-xs lg:text-sm text-white whitespace-nowrap transition-transform duration-300 group-hover:scale-110"
                  style={{ borderColor: `${spoke.color}55`, boxShadow: `0 0 22px ${spoke.color}22` }}
                >
                  <span className="inline-block w-1.5 h-1.5 rounded-full mr-2 align-middle animate-pulse-node" style={{ background: spoke.color }} />
                  {spoke.label}
                </div>
              </button>
            );
          })}
        </motion.div>

        {/* Mobile grid */}
        <div className="md:hidden grid grid-cols-2 gap-3" data-testid="ecosystem-hub-mobile">
          {SPOKES.map((spoke) => (
            <button
              key={spoke.label}
              onClick={() => setSelected(spoke)}
              className="glass rounded-xl px-4 py-3 flex items-center gap-2 text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-[#FF8800]"
              data-testid={`ecosystem-node-mobile-${spoke.label.toLowerCase()}`}
            >
              <span className="w-1.5 h-1.5 rounded-full animate-pulse-node shrink-0" style={{ background: spoke.color }} />
              <span className="font-display font-semibold text-sm text-white">{spoke.label}</span>
            </button>
          ))}
        </div>
      </div>

      <DetailModal
        item={
          selected && {
            title: selected.label,
            kicker: "LaunchLab Ecosystem Node",
            detail: selected.detail,
            bullets: selected.bullets,
            accent: selected.color,
          }
        }
        onClose={() => setSelected(null)}
      />
    </section>
  );
}
